<template>
  <div class="text-gray-dark">
    <div class="flex items-center">
      <base-switch id="repeatedUpdatesToggle"
                   color="success"
                   v-model="hasScheduler"
      />
      <label for="repeatedUpdatesToggle" class="ml-2 text-gray-dark">Repeated updates</label>
    </div>

    <transition name="slide-fade" mode="out-in">
      <div v-if="hasScheduler" :key="hasScheduler" class="mt-3">
        <div class="flex items-center gap-2">
          <base-select :value="selectedFrequencyOption"
                       :options="frequencyOptions"
                       text-attribute="label"
                       value-attribute="label"
                       :variant="isCreate ? 'baseSelectUpwards' : 'baseSelect'"
                       class="w-1/4 border-gray-border h-10"
                       @input="updateSelectedFrequencyOption"
          />
          <span>at</span>
          <time-input :value="time"
                      class="w-1/5 h-10"
                      placeholder="18:00"
                      @change="updateTime"
          />
          <span>{{ resolveUTCString() }}</span>
          <base-select v-if="isWeekly"
                       :value="cronDayOfWeek"
                       :options="dayOfWeekOptions"
                       text-attribute="label"
                       value-attribute="value"
                       :variant="isCreate ? 'baseSelectUpwards' : 'baseSelect'"
                       class="w-1/4 border-gray-border h-10"
                       @input="updateDayOfWeek"
          />
          <base-input v-if="isMonthly"
                      :key="selectedFrequencyOption"
                      :value="Number(cronDayOfMonth)"
                      :variant="'editor-centered'"
                      type="number"
                      class="w-1/5 border-gray-border"
                      :max="31"
                      :min="1"
                      :step="1"
                      @blur="validateDayOfMonthOnBlur"
          />
        </div>
        <div class="mt-1 text-sm">⚠️ Not recommended to reindex during peak hours.</div>
      </div>
    </transition>
  </div>
</template>

<script>
import Checkbox from '@/components/preview/Checkbox'
import TimeInput from '@/components/common/TimeInput'

const FREQUENCY_OPTION_DAILY = 'Daily'
const FREQUENCY_OPTION_WEEKLY = 'Weekly'
const FREQUENCY_OPTION_MONTHLY = 'Monthly'

export default {
  name: 'SettingsIndexScheduler',
  components: { Checkbox, TimeInput },
  props: {
    scheduleParams: {
      type: Object
    },
    isCreate: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    hasScheduler: {
      get() {
        return !!this.scheduleParams
      },
      set(hasScheduler) {
        const scheduleParams = hasScheduler ? { cronExpression: '30 03 * * 1' } : null
        this.updateScheduler(scheduleParams)
      }
    },
    frequencyOptions() {
      return [
        { label: FREQUENCY_OPTION_MONTHLY },
        { label: FREQUENCY_OPTION_WEEKLY },
        {
          label: FREQUENCY_OPTION_DAILY,
          disabled: !this.showUnreleased,
          showLock: true,
          tooltip: 'This option has extra costs.<br/>Contact support@amio.io to enable.'
        }
      ]
    },
    dayOfWeekOptions() {
      return [
        { label: 'Monday', value: '1' },
        { label: 'Tuesday', value: '2' },
        { label: 'Wednesday', value: '3' },
        { label: 'Thursday', value: '4' },
        { label: 'Friday', value: '5' },
        { label: 'Saturday', value: '6' },
        { label: 'Sunday', value: '0' }
      ]
    },
    dayOfMonthOptions() {
      return Array.from({ length: 31 }, (_, i) => ({ label: `${i + 1}` }))
    },
    cronExpressionSplit() {
      return this.scheduleParams?.cronExpression?.split(' ')
    },
    cronMinute() {
      return this.cronExpressionSplit?.[0]
    },
    cronHour() {
      return this.cronExpressionSplit?.[1]
    },
    cronDayOfMonth() {
      return this.cronExpressionSplit?.[2]
    },
    cronMonth() {
      return this.cronExpressionSplit?.[3]
    },
    cronDayOfWeek() {
      return this.cronExpressionSplit?.[4]
    },
    time() {
      return this.cronHour + ':' + this.cronMinute
    },
    selectedFrequencyOption() {
      if (!this.cronExpressionSplit) return null
      if (this.cronDayOfMonth !== '*') return FREQUENCY_OPTION_MONTHLY
      if (this.cronDayOfWeek !== '*') return FREQUENCY_OPTION_WEEKLY
      if (this.cronDayOfWeek === '*') return FREQUENCY_OPTION_DAILY

      return null
    },
    isWeekly() {
      return this.selectedFrequencyOption === FREQUENCY_OPTION_WEEKLY
    },
    isMonthly() {
      return this.selectedFrequencyOption === FREQUENCY_OPTION_MONTHLY
    }
  },
  methods: {
    updateSelectedFrequencyOption(option) {
      if (option === FREQUENCY_OPTION_DAILY) {
        const dailyCronExpression = `${this.cronMinute} ${this.cronHour} * * *`
        return this.updateScheduler({ cronExpression: dailyCronExpression })
      }
      
      if (option === FREQUENCY_OPTION_WEEKLY) {
        const weeklyCronExpression = `${this.cronMinute} ${this.cronHour} * * 1`
        return this.updateScheduler({ cronExpression: weeklyCronExpression })
      }

      if (option === FREQUENCY_OPTION_MONTHLY) {
        const monthlyCronExpression = `${this.cronMinute} ${this.cronHour} 1 * *`
        return this.updateScheduler({ cronExpression: monthlyCronExpression })
      }
    },
    updateTime(time) {
      const [hours, mins] = time.split(':')
      const cronExpression = `${mins} ${hours} ${this.cronDayOfMonth} ${this.cronMonth} ${this.cronDayOfWeek}`
      this.updateScheduler({ cronExpression: cronExpression })
    },
    updateDayOfWeek(dayOfWeek) {
      const cronExpression = `${this.cronMinute} ${this.cronHour} ${this.cronDayOfMonth} ${this.cronMonth} ${dayOfWeek}`
      this.updateScheduler({ cronExpression: cronExpression })
    },
    validateDayOfMonthOnBlur({ target }) {
      const { value, min, max } = target
      const dayOfMonth = this.resolveRange(value, min, max)
      this.updateDayOfMonth(dayOfMonth)
    },
    resolveRange(value, min, max) {
      if (+value > max) return max
      if (+value < min) return min

      return +value
    },
    resolveUTCString() {
      if (this.isMonthly) return 'UTC on day'
      if (this.isWeekly) return 'UTC on'
      return 'UTC'
    },
    updateDayOfMonth(dayOfMonth) {
      const cronExpression = `${this.cronMinute} ${this.cronHour} ${dayOfMonth} ${this.cronMonth} ${this.cronDayOfWeek}`
      this.updateScheduler({ cronExpression: cronExpression })
    },
    updateScheduler(scheduleParams) {
      this.$emit('update-schedule-params', scheduleParams)
    }
  }
}
</script>